import { makeAutoObservable } from "mobx";
import { createContext, useContext } from "react";
import type {
  OrderCheckboxData,
  OrderDetails,
  OrderFlow,
  OrderProductData,
} from "./types/order";
import {
  createDefaultOrderDetails,
  createOrderCheckboxData,
} from "./types/order";
import type { RegisterDetailsModel } from "./types/register";
import { createDefaultRegisterDetails } from "./types/register";
import { provinceFullNameById, type Province } from "./types/province";
import type { LanguageType } from "./types/language";
import type {
  OrderContactDto,
  IndigenousCommunitySimpleListDto,
  OrderCreatedDto,
  OrderValidateDto,
  ProductCategoryDto,
} from "@/api/types";
import { formatCurrencyByLanguage } from "@/helpers/language";
import type { TrainingData } from "./types/training";
import { createTrainingData } from "./types/training";
import { Ga4Store } from "./Ga4Store";
import { TelemetryStore } from "./TelemetryStore";
import { translations } from "@/constants/translations";
import { otherOrganizationType } from "@/constants/order";

export class RootStore {
  ga4Store: Ga4Store;
  telemetryStore: TelemetryStore;

  activeLanguage: LanguageType = "english";
  activeProvinceId?: Province = undefined;
  activeIndigenousCommunity?: IndigenousCommunitySimpleListDto = undefined;
  activeProductCategory?: ProductCategoryDto = undefined;

  productCategories: ProductCategoryDto[] = [];

  orderData: OrderDetails = createDefaultOrderDetails();
  orderContactData?: OrderContactDto = undefined;
  orderCheckboxData = createOrderCheckboxData();
  orderCreatedData?: OrderCreatedDto = undefined;
  orderValidatedData?: OrderValidateDto = undefined;

  registerData: RegisterDetailsModel = createDefaultRegisterDetails();
  leavingModalUrl: string = "";
  trainingData: TrainingData = createTrainingData();

  constructor() {
    this.ga4Store = new Ga4Store(this);
    this.telemetryStore = new TelemetryStore(this);
    makeAutoObservable(this);
  }

  get translations() {
    return window.translations;
  }

  get activeProvinceTranslated() {
    if (!this.activeProvinceId) return "";

    const translations =
      this.translations.common.provinces[this.activeLanguage];
    return translations[this.activeProvinceId];
  }

  get activeProvinceFullName() {
    return this.activeProvinceId
      ? provinceFullNameById[this.activeProvinceId]
      : undefined;
  }

  get isEmergentFlow() {
    return (
      this.orderData.ada != null &&
      this.orderData.ada !== "" &&
      !this.isIndigenousFlow
    );
  }

  get isIndigenousFlow() {
    return this.activeIndigenousCommunity != null;
  }

  get isOntarioFlow() {
    return this.activeProvinceId === "ON";
  }

  get activeFlow(): OrderFlow {
    if (this.isIndigenousFlow) return "indigenous";
    if (this.isEmergentFlow) return "emergent";
    if (this.isOntarioFlow) return "ontario";
    return "standard";
  }

  get isValidOrderMinimum() {
    const hasInvalid = this.orderProducts.some(
      (p) => p.quantity !== 0 && p.quantity < p.minimumQuantity,
    );

    const hasAtLeastOneProduct = this.orderProducts.some((p) => p.quantity > 0);

    return !hasInvalid && hasAtLeastOneProduct;
  }

  get productById() {
    return this.productCategories.reduce(
      (acc, category) => {
        category.products.forEach((product) => {
          acc[product.id] = product;
        });
        return acc;
      },
      {} as Record<number, ProductCategoryDto["products"][number]>,
    );
  }

  get orderProducts(): OrderProductData[] {
    const language = this.translations.common.products[this.activeLanguage];

    return this.orderData.products.map((p) => {
      const data = this.productById[p.id];
      const price = data.price;
      const total = price * p.quantity;
      const productLanguage = language[data.id];

      return {
        ...data,
        quantity: p.quantity,
        total,
        language: {
          items: productLanguage.items,
          itemsDescLabel: productLanguage.items_desc,
          numberOfLabel: productLanguage.number_of,
          pricePerLabel: productLanguage.price_per,
          minimumOrder: this.activeIndigenousCommunity
            ? productLanguage.minimum_order_indigenous
            : productLanguage.minimum_order,
          imageSrc: productLanguage.imageSrc,
        },
        totalFormatted: this.getFormattedCurrency(total),
        priceFormatted: this.getFormattedCurrency(price),
      };
    });
  }

  get orderTotals() {
    const subTotal = this.orderProducts.reduce((amount, p) => {
      const newAmount = amount + p.total;
      return newAmount;
    }, 0);

    const discountPercent = this.orderData.promotionalDiscountAmount || 0;
    const discount = (discountPercent / 100) * subTotal;
    const subtotalWithDiscount = subTotal - discount;

    const total =
      subtotalWithDiscount + this.orderData.shippingCost + this.orderData.tax;

    return {
      discount,
      subTotal,
      subtotalWithDiscount,
      shippingCost: this.orderData.shippingCost,
      tax: this.orderData.tax,
      total,
      formatted: {
        discount: `-${this.getFormattedCurrency(discount)}`,
        subtotal: this.getFormattedCurrency(subTotal),
        subtotalWithDiscount: this.getFormattedCurrency(subtotalWithDiscount),
        tax: this.getFormattedCurrency(this.orderData.tax),
        total: this.getFormattedCurrency(total),
      },
    };
  }

  get chaseUrl() {
    if (!this.orderCreatedData) return "";
    return this.orderCreatedData.paymentUrl + this.orderCreatedData.externalId;
  }

  get shippingProvinceLabel() {
    const commonLanguage = translations.common["provinces-fullName"][
      this.activeLanguage
    ] as any;

    const province = this.orderData.shippingProvince;
    return commonLanguage[province] || province;
  }

  get billingProvinceLabel() {
    const commonLanguage = translations.common["provinces-fullName"][
      this.activeLanguage
    ] as any;

    const province = this.orderData.billingProvince;
    return commonLanguage[province] || province;
  }

  get organizationTypeLabel() {
    const commonLanguage = translations.common["organization-types"][
      this.activeLanguage
    ] as any;

    const { organizationType, organizationTypeOther } = this.orderData;

    if (!organizationType) return "";

    if (organizationType === otherOrganizationType)
      return organizationTypeOther || "";

    return commonLanguage[organizationType] || organizationType;
  }

  getShippingCostFormatted = () => {
    const language =
      this.translations.pages["order-summary"][this.activeLanguage];

    const formatted = this.getFormattedCurrency(this.orderData.shippingCost);

    if (this.isIndigenousFlow) return formatted;
    return this.orderData.shippingCost ? formatted : language.free;
  };

  getFormattedCurrency = (value: number, includeDecimals: boolean = true) => {
    return formatCurrencyByLanguage(
      value,
      this.activeLanguage,
      includeDecimals,
    );
  };

  getErrorInputText = (text: string) => {
    const translation =
      this.translations.common.form[this.activeLanguage].is_required;
    return `${text} ${translation}`;
  };

  setActiveLanguage = (value: LanguageType) => {
    this.activeLanguage = value;
    this.orderData.language = value;
    this.registerData.language = value;
  };

  setActiveProvinceId = (province?: Province) => {
    this.activeProvinceId = province;
  };

  setProductCategories = (data: ProductCategoryDto[]) => {
    this.productCategories = data;

    if (data.length > 0) this.setOrderProductCategory(data[0]);
  };

  setActiveIndigenousCommunity = (
    community?: IndigenousCommunitySimpleListDto,
  ) => {
    this.activeIndigenousCommunity = community;
  };

  setOrderData = (data: OrderDetails) => {
    this.orderData = data;
  };

  setOrderProductCategory = (category?: ProductCategoryDto) => {
    this.activeProductCategory = category;

    if (category) {
      this.orderData.products = category.products.map((p) => ({
        id: p.id,
        quantity: 0,
      }));
    }
  };

  setOrderProductAmount = (productId: number, amount: number) => {
    const product = this.orderData.products.find((p) => p.id === productId);

    if (product) product.quantity = amount;
  };

  setOrderContactData = (data: OrderContactDto) => {
    this.orderContactData = data;
  };

  setOrderCreatedData = (data: OrderCreatedDto) => {
    this.orderCreatedData = data;
  };

  setOrderValidatedData = (data: OrderValidateDto) => {
    this.orderValidatedData = data;
  };

  setRegisterData = (data: RegisterDetailsModel) => {
    this.registerData = data;
  };

  showLeavingModal = (url: string) => {
    this.leavingModalUrl = url;
  };

  closeLeavingModal = () => {
    this.leavingModalUrl = "";
  };

  toggleOrderCheckbox = (key: keyof OrderCheckboxData) => {
    this.orderCheckboxData[key] = !this.orderCheckboxData[key];
  };

  getProvincePrefix = (province: Province) => {
    return this.translations.common["province-prefix"][this.activeLanguage][
      province
    ];
  };

  reset = () => {
    this.orderData = createDefaultOrderDetails();
    this.registerData = createDefaultRegisterDetails();
    this.orderCheckboxData = createOrderCheckboxData();
    this.productCategories = [];
    this.activeProductCategory = undefined;
    this.activeProvinceId = undefined;
    this.activeIndigenousCommunity = undefined;
  };
}

export const rootStore = new RootStore();

const StoreContext = createContext<RootStore>({} as RootStore);
export const StoreProvider = StoreContext.Provider;
export const useStore = (): RootStore => useContext(StoreContext);
